import React, { useEffect, useState, useRef } from "react";
import { Button as AntBtn, Card, Col, Empty, Form, Row, message } from "antd";
import { useForm, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";

import Button from "../../../molecules/Button";
import { Add } from "../../../../assets/svg";
import { apiV1Endpoint } from "../../../../api/v1";
import { getBookingDetail } from "../../../modules/BookingForm/ducks/actions";
import { AppDispatch } from "../../../../redux/store";
import { AdditionalTermsFields } from "../../DynamicFields/AdditionalTermsFields";

type AdditionalTerm = {
  english: string;
  arabic: string;
};

type AdditionalTermsFormValues = {
  additionalTerms: AdditionalTerm[];
};

interface LeaseConfigProps {
  onToggle?: (value: boolean) => void;
  isReadOnly?: boolean;
}

const MAX_TERMS = 10;

const AdditionalTerms: React.FC<LeaseConfigProps> = ({
  onToggle,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseId = bookingDetails?.leaseId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const initializedLeaseId = useRef<string | null>(null);

  const { control, setValue, handleSubmit, reset } =
    useForm<AdditionalTermsFormValues>({
      defaultValues: {
        additionalTerms: [],
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalTerms",
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!leaseId || hasInitialized.current) return;

    const backendTerms = bookingDetails?.additionalterms;

    if (Array.isArray(backendTerms) && backendTerms.length > 0) {
      reset({
        additionalTerms: backendTerms.map((t: any) => ({
          english: t.english || "",
          arabic: t.arabic || "",
        })),
      });
    } else {
      reset({
        additionalTerms: [],
      });
    }

    hasInitialized.current = true;
  }, [leaseId, reset]);

  const handleAddTerm = () => {
    if (fields.length >= MAX_TERMS || isReadOnly) return;
    append({ english: "", arabic: "" });
  };

  const isAddDisabled = fields.length >= MAX_TERMS || isReadOnly;

  const onSubmit = async (data: AdditionalTermsFormValues) => {
    try {
      setIsLoading(true);

      const payload = {
        additionalTerms: data.additionalTerms.map((term, index) => ({
          term: index + 1,
          english: term.english,
          arabic: term.arabic,
        })),
      };

      await axios.patch(
        `${apiV1Endpoint}/booking/${leaseId}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      message.success("Additional terms saved successfully");
      dispatch(getBookingDetail(leaseId));
      onToggle?.(false);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Please try again");
    } finally {
      setIsLoading(false);
    }
  };

  if (!bookingDetails) {
    return <Empty />;
  }

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <h3 style={{ margin: 0 }}>Add Additional Terms to the Contract</h3>
        </Col>
        <Col>
          <span style={{ color: "#8c8c8c" }}>
            {fields.length}/{MAX_TERMS} terms
          </span>
        </Col>
      </Row>

      <Card style={{ maxHeight: "80vh", overflow: "auto" }}>
        {fields.length === 0 ? (
          <Empty description="No terms added. Click 'Add Term' to create one." />
        ) : (
          <AdditionalTermsFields
            fields={fields}
            control={control}
            setValue={setValue}
            remove={remove}
            isReadOnly={isReadOnly}
          />
        )}
      </Card>

      <Row justify="space-between" style={{ marginTop: 24 }}>
        <Button
          label="Add Term"
          icon={<Add width="18px" height="18px" style={{ marginRight: "8px" }} />}
          onClick={handleAddTerm}
          disabled={isAddDisabled}
          className="change-button"
        />

        <AntBtn
          type="primary"
          htmlType="submit"
          size="large"
          disabled={isLoading || isReadOnly}
          className="csv_publish"
        >
          {isLoading ? "Saving..." : "Save Terms"}
        </AntBtn>
      </Row>
    </Form>
  );
};

export default AdditionalTerms;
